import { yupResolver } from "@hookform/resolvers/yup"
import { useEffect, useState } from "react"
import * as yup from "yup"

import Button from "@components/button"
import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { useCustomerPageContext } from "@contexts/customer"
import {
  deleteSpecialOffer,
  updateMutationSpecialOffer
} from "@domain/customer/infos"
import useControlledForm from "@hooks/use_controlled_form"
import DeleteIcon from "@mui/icons-material/Delete"
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack
} from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"
import { Product } from "__generated__/graphql"

// api-v2 `OPERATOR_FREE_CREDITS` for a Special plan with none set.
const DEFAULT_SPECIAL_OPERATOR_CREDITS = 500

export type OfferFormData = {
  person: number
  whatsappBusiness: number
  userSeat: number
  monthlyPrice: number
  yearlyPrice: number
  operatorCredits: number
}

export default function UpdateSpecialOffer({
  isOpen,
  setClose,
  products
}: {
  isOpen: boolean
  setClose: () => void
  products?: Product[]
}) {
  const { translate, currentCustomer: customer } = useCustomerPageContext()

  const queryClient = useQueryClient()

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)

  const schema = yup
    .object({
      person: yup.number().required(),
      whatsappBusiness: yup.number().required(),
      userSeat: yup.number().required(translate("user_seat_field_is_required")),
      monthlyPrice: yup
        .number()
        .required(translate("monthly_price_field_is_required")),
      yearlyPrice: yup
        .number()
        .required(translate("yearly_price_field_is_required")),
      operatorCredits: yup.number().min(1).required()
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid }
  } = useControlledForm<OfferFormData>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      person: 0,
      whatsappBusiness: 0,
      userSeat: 0,
      monthlyPrice: 0,
      yearlyPrice: 0,
      operatorCredits: DEFAULT_SPECIAL_OPERATOR_CREDITS
    }
  })

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen && products) {
      const product = products.find((item) => item.name === "Special")
      if (product) {
        reset({
          person: product.limits?.person || 0,
          whatsappBusiness: product.limits?.whatsAppBusiness || 0,
          userSeat: product.limits?.cPerson || 0,
          monthlyPrice: product.monthlyPrice || 0,
          yearlyPrice: product.yearlyPrice || 0,
          operatorCredits:
            (product.limits as { operatorCredits?: number | null } | null)
              ?.operatorCredits || DEFAULT_SPECIAL_OPERATOR_CREDITS
        })
      } else {
        reset({
          person: 0,
          whatsappBusiness: 0,
          userSeat: 0,
          monthlyPrice: 0,
          yearlyPrice: 0,
          operatorCredits: DEFAULT_SPECIAL_OPERATOR_CREDITS
        })
      }
    }
  }, [isOpen, products, reset])

  async function onSubmit(data: OfferFormData) {
    if (!customer?.package?.customer) {
      return
    }
    setLoading(true)
    const { errorMessage } = await updateMutationSpecialOffer({
      cPerson: data.userSeat,
      customerId: customer._id,
      monthlyPrice: data.monthlyPrice,
      person: data.person,
      whatsAppBusiness: data.whatsappBusiness,
      yearlyPrice: data.yearlyPrice,
      operatorCredits: data.operatorCredits
    })
    if (!!errorMessage) {
      showErrorToast(String(errorMessage))
    }
    setLoading(false)
    handleClose()
    queryClient.invalidateQueries({ queryKey: ["special-offer"] })
  }

  async function handleDeleteSpecialOffer() {
    if (!customer?.package?.customer) {
      return
    }
    setLoading(true)
    const { errorMessage } = await deleteSpecialOffer({
      customerId: customer._id as string
    })
    if (!!errorMessage) {
      showErrorToast(String(errorMessage))
    }
    setLoading(false)
    handleClose()
    queryClient.invalidateQueries({ queryKey: ["special-offer"] })
  }

  function handleClose() {
    if (loading) {
      return
    }
    setClose()
    reset()
  }
  return (
    <>
      <DialogProvider
        title={translate("update_special_offer")}
        leftButton={{
          text: translate("cancel"),
          color: "secondary",
          onClick: setClose,
          disabled: loading
        }}
        rightButton={{
          text: translate("save"),
          color: "primary",
          onClick: handleSubmit(onSubmit),
          loading: loading,
          disabled: !isValid
        }}
        isOpen={isOpen}
        setClose={handleClose}
        size="small"
      >
        <Stack gap={1}>
          <TextInput<OfferFormData>
            type="number"
            label={translate("customer_person")}
            disabled={loading}
            name="person"
            placeholder={translate("enter")}
            control={control}
          />
          <TextInput<OfferFormData>
            type="number"
            label={"Whatsapp bussiness"}
            disabled={loading}
            name="whatsappBusiness"
            placeholder={translate("enter")}
            control={control}
          />
          <TextInput<OfferFormData>
            type="number"
            label={translate("user_seat")}
            disabled={loading}
            name="userSeat"
            placeholder={translate("enter")}
            control={control}
          />
          <TextInput<OfferFormData>
            type="number"
            label={translate("operator_monthly_credits")}
            disabled={loading}
            name="operatorCredits"
            placeholder={translate("enter")}
            control={control}
          />
          <TextInput<OfferFormData>
            type="number"
            TextLeft="$"
            label={translate("monthly_price")}
            disabled={loading}
            name="monthlyPrice"
            placeholder={translate("enter")}
            control={control}
          />
          <TextInput<OfferFormData>
            type="number"
            TextLeft="$"
            label={translate("yearly_price")}
            disabled={loading}
            name="yearlyPrice"
            placeholder={translate("enter")}
            control={control}
          />
          {customer?.package?.name !== "Special" && (
            <IconButton
              disableRipple
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={loading}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Stack>
      </DialogProvider>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle textAlign="center">
          {translate("delete_special_offer")}
        </DialogTitle>
        <DialogContent>{translate("delete_offer_summary")}</DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-around"
          }}
        >
          <Button onClick={() => setConfirmDeleteOpen(false)} color="primary">
            {translate("cancel")}
          </Button>
          <Button
            onClick={() => {
              handleDeleteSpecialOffer()
              setConfirmDeleteOpen(false)
            }}
            color="error"
          >
            {translate("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
